import * as React from "react";
import { type ColumnDef } from "@tanstack/react-table";

import { DataTable } from "@/components/ui/data-table";
import type { PayoutRequest } from "@/services/payout.service";
import type { StatValue } from "@/types/dashboard";
import { formatAmount } from "@/utils/formatters";
import { DashboardCard, StatusText } from "@/components/dashboard/dashboard-card";
import { shortDate } from "./helpers";

const columns: ColumnDef<PayoutRequest>[] = [
  {
    accessorKey: "name",
    header: "Affiliate",
    meta: { width: "w-44" },
    cell: ({ row }) => (
      <span className="whitespace-nowrap font-medium text-foreground">
        {row.original.name}
      </span>
    ),
  },
  {
    accessorKey: "amount",
    header: "Amount",
    meta: { width: "w-24" },
    cell: ({ row }) => (
      <span className="tabular-nums text-foreground">
        {formatAmount(Number(row.original.amount) || 0)}
      </span>
    ),
  },
  {
    accessorKey: "paymentMethod",
    header: "Method",
    meta: { width: "w-28" },
    cell: ({ row }) => (
      <span className="truncate text-foreground">
        {row.original.paymentMethod ?? "—"}
      </span>
    ),
  },
  {
    accessorKey: "status",
    header: "Status",
    meta: { width: "w-24" },
    cell: ({ row }) => <StatusText status={row.original.status} />,
  },
  {
    accessorKey: "createdAt",
    header: "Requested",
    meta: { width: "w-24" },
    cell: ({ row }) => (
      <span className="whitespace-nowrap text-muted-foreground">
        {shortDate(row.original.createdAt)}
      </span>
    ),
  },
];

const MAX_ROWS = 5;

export function PayoutRequestsCard({
  data,
  stats,
  isLoading,
}: {
  data: PayoutRequest[] | undefined;
  stats?: StatValue[];
  isLoading: boolean;
}) {
  const rows = React.useMemo(
    () =>
      [...(data ?? [])]
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
        .slice(0, MAX_ROWS),
    [data]
  );

  return (
    <DashboardCard
      id="dashboard-payout-requests-card"
      title="Payout requests"
      to="/brand/payouts"
      stats={stats}
      bodyClassName="px-3 pb-3"
    >
      <DataTable
        columns={columns}
        data={rows}
        isLoading={isLoading}
        skeletonRows={3}
        getRowId={(row) => row.id}
        emptyText="No payout requests yet"
        minTableWidth="min-w-[30rem]"
      />
    </DashboardCard>
  );
}
